import React, { useState, useEffect } from "react";
import { MoreVertical, Phone, Trash2, FileDown, MoreHorizontal, Mail, X, Pencil } from "lucide-react";
import ScheduleInterview from "./ScheduleInterview";
import QuestionForm from "./QuestionForm";
import { toast } from "react-toastify";
import OfferLetterModal from "./OfferLetterModal";
import DecisionModal from "./DecisionModal";
import Joining from "./Joining";
import CompanyAssets from "./CompanyAssests";
import { decryptToken } from "../utils/getToken";

const DeleteConfirmationModal = ({ candidate, onConfirm, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center">
      <div className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Confirm Deletion</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 text-sm font-medium"
          >
            <X />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to delete {candidate.CandidateName}?
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(candidate.id)}
            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const OnboardingCards = ({ candidates = [], refreshCandidates }) => {
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [interviewTakenMap, setInterviewTakenMap] = useState({});
  const [scheduledMap, setScheduledMap] = useState({});
  const [showDecision, setShowDecision] = useState(false);
  const [decisionData, setDecisionData] = useState({ decision: "", feedback: "" });
  const [decisionMap, setDecisionMap] = useState({});
  const [offerSentMap, setOfferSentMap] = useState({});
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [currentCandidateForPDF, setCurrentCandidateForPDF] = useState(null);
  const [offersById, setOffersById] = useState({});



  const [offerData, setOfferData] = useState({
    Salary: "",
    Address: "",
    ReportingManager: "",
    JoiningDate: "",
    WorkingHours: "",
    StartTime: "",
    EndTime: "",
    NatureofEmployment: "",
    OtherEmploymentDetail: "",
    SubjecttoTermination: "",
    ProbationPeriod: ""
  });

  const [showJoiningForm, setShowJoiningForm] = useState(false);
  const [joinedMap, setJoinedMap] = useState({});
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null)
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = decryptToken("accessToken") || ""


  const apiUrl = process.env.REACT_APP_LINK;

  const handleDelete = async (id) => {
    const candidate = candidates.find(c => c.id === id);
    if (!candidate) return;
    setCandidateToDelete(candidate);
    setShowDeleteModal(true); // Show the delete confirmation modal
  };

  const confirmDelete = async (id) => {
    try {
      const res = await fetch(`${apiUrl}/career/${id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (res.ok) {
        toast.success("Candidate deleted successfully!");
        if (refreshCandidates) refreshCandidates();
        setShowDeleteModal(false);
        setCandidateToDelete(null);
      } else {
        toast.error("Failed to delete candidate");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Error deleting candidate");
    }
  };

  const fetchSchedules = async () => {
    try {
      const res = await fetch(`${apiUrl}/schedule`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        const map = {};
        data.forEach(item => {
          const email = item.CandidateEmail?.toLowerCase();
          if (email) map[email] = true;
        });
        setScheduledMap(map);
      }
    } catch (err) {
      console.error("Error fetching schedules", err);
    }
  };

  const handleScheduleClick = (candidate) => {
    const emailKey = candidate.CandidateEmail?.toLowerCase();
    if (scheduledMap[emailKey]) {
      toast.info("Interview already scheduled for this candidate.");
      return;
    }

    setSelectedCandidate(candidate);
    setShowSchedule(true);
  };

  const openDecisionModal = (candidate) => {
    setSelectedCandidate(candidate);
    setShowDecision(true);
    setDecisionData({ decision: "", feedback: "" });
  };

  const handleDecisionChange = (e) => {
    const { name, value } = e.target;
    setDecisionData(prev => ({ ...prev, [name]: value }));
  };

  const handleDecisionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/decision`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          CandidateEmail: selectedCandidate.CandidateEmail,
          CandidateName: selectedCandidate.CandidateName,
          Decision: decisionData.decision,
          Feedback: decisionData.feedback
        })
      });

      if (res.ok) {
        toast.success("Decision submitted and email sent!");

        // ✅ Update decision map without needing a full re-fetch
        setDecisionMap(prev => ({
          ...prev,
          [selectedCandidate.CandidateEmail.toLowerCase()]: decisionData.decision
        }));

        setShowDecision(false);
      } else {
        toast.error("Failed to send decision.");
      }
    } catch (err) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDecisions = async () => {
    try {
      const res = await fetch(`${apiUrl}/decision`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        const map = {};
        data.forEach(decision => {
          const email = decision.CandidateEmail?.toLowerCase();
          if (email) map[email] = decision.Decision;
        });
        setDecisionMap(map);
      }
    } catch (err) {
      console.error("Error fetching decisions", err);
    }
  };

  const openOfferForm = (candidate) => {
    setSelectedCandidate(candidate);
    setShowOfferForm(true);
  };

  const openOfferLetter = (candidate) => {
    const offer = offersById[candidate.id];
    if (!offer || !offer.pdfFileName) {
      toast.error("Offer PDF not found for this candidate.");
      return;
    }
    // Keep only what's needed for the modal
    setCurrentCandidateForPDF({
      CandidateName: candidate.CandidateName,
      pdfFileName: offer.pdfFileName,
    });
    setShowOfferModal(true);
  };


  const closeForm = () => {
    setShowOfferForm(false);
    setSelectedCandidate(null);
    setOfferData({
      Salary: "",
      Address: "",
      ReportingManager: "",
      JoiningDate: "",
      WorkingHours: "",
      StartTime: "",
      EndTime: "",
      NatureofEmployment: "",
      OtherEmploymentDetail: "",
      SubjecttoTermination: "",
      ProbationPeriod: ""
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOfferData(prev => ({ ...prev, [name]: value }));
  };

  const fetchOfferLetters = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/offerletter`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        const sentMap = {};
        const byId = {};
        data.forEach((offer) => {
          if (offer.CandidateEmail) {
            sentMap[offer.CandidateEmail.toLowerCase()] = true;
          }
          if (offer.id) {
            byId[offer.id] = offer; // <-- keep full offer incl. pdfFileName
          }
        });
        setOfferSentMap(sentMap);
        setOffersById(byId); // <-- new
      }
    } catch (err) {
      console.error("Error fetching offer letters", err);
    } finally {
      setLoading(false);
    }
  };


  function OfferLetterViewer({ candidate }) {
    const pdfUrl = `${process.env.REACT_APP_LINK}/offerletter/${candidate.id}/pdf`;
    return (
      <div className="pdf-viewer">
        <iframe
          src={pdfUrl}
          width="100%"
          height="600px"
          title={`Offer Letter for ${candidate.CandidateName}`}
        />
      </div>
    );
  }


  // const fetchInterviewTaken = async () => {
  //   try {
  //     const res = await fetch(`${apiUrl}/question`);
  //     const data = await res.json();
  //     if (res.ok) {
  //       const map = {};
  //       data.forEach(item => {
  //         const email = item.CandidateEmail?.toLowerCase();
  //         if (email && item.IsInterviewTaken) {
  //           map[email] = true;
  //         }
  //       });
  //       setInterviewTakenMap(map);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching interview taken data", err);
  //   }
  // };

  const fetchInterviewTaken = async () => {
    try {
      const res = await fetch(`${apiUrl}/question`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        const map = {};
        data.forEach(item => {
          const email = item.CandidateEmail?.toLowerCase();
          if (email && item.IsInterviewTaken) {
            map[email] = true;
          }
        });
        setInterviewTakenMap(map);
      }
    } catch (err) {
      console.error("Error fetching interview taken data", err);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      CandidateName: selectedCandidate.CandidateName,
      CandidateEmail: selectedCandidate.CandidateEmail,
      Role: selectedCandidate.Role,
      Salary: offerData.Salary,
      Address: offerData.Address,
      ReportingManager: offerData.ReportingManager,
      JoiningDate: offerData.JoiningDate,

      WorkingHours: offerData.WorkingHours,
      StartTime: offerData.StartTime,
      EndTime: offerData.EndTime,
      NatureofEmployment: offerData.NatureofEmployment,
      OtherEmploymentDetail: offerData.OtherEmploymentDetail,
      SubjecttoTermination: offerData.SubjecttoTermination,
      ProbationPeriod: offerData.ProbationPeriod

    };


    try {
      const res = await fetch(`${apiUrl}/offerletter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success("Offer Letter Submitted and Email Sent Successfully!");

        setOfferSentMap(prev => ({
          ...prev,
          [selectedCandidate.CandidateEmail.toLowerCase()]: true
        }));

        closeForm();
      }
    } catch {
      toast.error("Something went wrong while submitting the offer letter.");
    }
  };
  const fetchJoinings = async () => {
    try {
      const res = await fetch(`${apiUrl}/joining`,{
        method:"GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();

      if (res.ok) {
        const map = {};

        data.forEach((join) => {
          const email = join.CandidateEmail?.toLowerCase();

          if (email && join.IsJoined === "Yes") {
            map[email] = {
              joiningDate: join.JoiningDate ? new Date(join.JoiningDate).toISOString() : null,
              pdfFileName: join.pdfFileName || join.PdfFileName || join.appointmentLetter || null, // ✅ handle multiple cases
            };
          }
        });

        console.log("JOINED MAP BUILT:", map); // 🔎 Debug
        setJoinedMap(map);
      } else {
        console.error("Failed to fetch joinings:", data);
      }
    } catch (err) {
      console.error("Error fetching joining records", err);
    }
  };




  useEffect(() => {
    fetchSchedules();
    fetchDecisions();
    fetchOfferLetters();
    fetchJoinings();
    fetchInterviewTaken();   // ✅ new line

  }, []);

  return (
    <div className="space-y-3">
      {candidates.map((candidate) => {
        const appliedDate = new Date(candidate.createdAt);
        const formattedApplied = appliedDate.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const formattedTime = appliedDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        });
        const isOfferSent = offerSentMap[candidate.CandidateEmail?.toLowerCase()] === true;
        const decisionStatus = decisionMap[candidate.CandidateEmail?.toLowerCase()];

        return (
          <div
            key={candidate._id || candidate.CandidateEmail}
            className={`shadow-sm rounded-lg p-3 relative border 
    ${decisionStatus === "Selected"
                ? "bg-green-100 border-green-300"
                : decisionStatus === "Rejected"
                  ? "bg-red-100 border-red-300"
                  : interviewTakenMap[candidate.CandidateEmail?.toLowerCase()]
                    ? "bg-orange-100 border-orange-300"
                    : scheduledMap[candidate.CandidateEmail?.toLowerCase()]
                      ? "bg-yellow-100 border-yellow-300"
                      : "bg-white border-gray-200"
              }`}
          >

            <div className="mb-2">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                {candidate.CandidateName}

                {decisionStatus === "Rejected" ? (
                  <span className="px-2 text-[10px] rounded-full font-medium bg-red-800 text-red-100">
                    Rejected
                  </span>
                ) : decisionStatus === "Selected" ? (
                  joinedMap[candidate.CandidateEmail?.toLowerCase()] ? (
                    <span className="px-2 text-[10px] rounded-full font-medium bg-purple-800 text-purple-100">
                      Joined
                    </span>
                  ) : isOfferSent ? (
                    <span className="px-2 text-[10px] rounded-full font-medium bg-blue-800 text-blue-100">
                      Offer Sent
                    </span>
                  ) : (
                    <span className="px-2 text-[10px] rounded-full font-medium bg-sky-800 text-sky-100">
                      Offer Not Sent
                    </span>
                  )
                ) : interviewTakenMap[candidate.CandidateEmail?.toLowerCase()] ? (

                  <span className="px-2 text-[10px] rounded-full font-medium bg-orange-800 text-orange-100">
                    Interview Taken
                  </span>
                ) : scheduledMap[candidate.CandidateEmail?.toLowerCase()] ? (
                  <span className="px-2 text-[10px] rounded-full font-medium bg-yellow-800 text-yellow-100">
                    Scheduled
                  </span>
                ) : (
                  <span className="px-2 text-[10px] rounded-full font-medium bg-gray-800 text-gray-100">
                    New
                  </span>
                )}

              </p>

              <p className="text-xs font-semibold text-blue-600">{candidate.Role || "N/A"}</p>
            </div>


            <hr className="my-2" />

            {/* Experience */}
            <p className="text-xs text-gray-500">
              Experience: <span className="font-medium text-gray-800">{candidate.Experience || "0"} yrs</span>
            </p>

            {/* 3 Column Info */}
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
              <div>
                <p className="text-gray-500">Email</p>
                <a
                  href={`mailto:${candidate.CandidateEmail}`}
                  className="text-black flex items-center gap-1"
                >
                  {candidate.CandidateEmail}
                </a>
              </div>
              <div>
                <p className="text-gray-500">Phone</p>
                <a
                  href={`tel:${candidate.CandidatePhone}`}
                  className="text-black flex items-center gap-1"
                >
                  {candidate.CandidatePhone}
                </a>
              </div>
              <div>
                <p className="text-gray-500">Resume</p>
                {candidate.Resume ? (
                  <a
                    href={`${apiUrl}/uploads/resumes/${candidate.Resume}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black flex items-center gap-1"
                  >
                    View Resume
                  </a>
                ) : (
                  <span className="text-gray-400">N/A</span>
                )}
              </div>
              <div>
                <p className="text-gray-500">Address</p>
                <p className="text-black">
                  {candidate.Address}
                </p>
              </div>
            </div>

            {/* Applied On */}
            <p className="text-[11px] text-gray-500 mt-3">
              Applied on {formattedApplied}, {formattedTime}
            </p>
            {joinedMap[candidate.CandidateEmail?.toLowerCase()] && (
              <p className="text-[11px] text-gray-500 mt-1">
                Joined on{" "}
                {new Date(joinedMap[candidate.CandidateEmail.toLowerCase()].joiningDate).toLocaleDateString(
                  undefined,
                  { year: "numeric", month: "short", day: "numeric" }
                )}
              </p>
            )}




            {/* Kebab Menu */}
            <div className="absolute top-3 right-2">
              <div className="relative">
                {/* Trigger Button */}
                <div
                  className="p-1.5 rounded-full hover:bg-gray-100 cursor-pointer"
                  onClick={() =>
                    setSelectedCandidate(
                      selectedCandidate?.CandidateEmail === candidate.CandidateEmail
                        ? null
                        : candidate
                    )
                  }
                >
                  <MoreHorizontal className="w-5 h-5 text-gray-600" />
                </div>

                {/* Dropdown */}
                {selectedCandidate?.CandidateEmail === candidate.CandidateEmail && (
                  <div className="absolute right-0 mt-2 bg-white border border-gray-200 shadow-md rounded-lg w-44 z-50 overflow-hidden">
                    <div
                      className={`px-3 py-2 text-xs flex items-center gap-1 transition-colors
    ${scheduledMap[candidate.CandidateEmail?.toLowerCase()]
                          ? "bg-yellow-100 text-yellow-800"
                          : "hover:bg-gray-50"
                        } cursor-pointer`}
                      onClick={() => {
                        if (scheduledMap[candidate.CandidateEmail?.toLowerCase()]) {
                          // Reschedule allowed → directly open
                          setSelectedCandidate(candidate);
                          setShowSchedule(true);
                        } else {
                          // Schedule → directly open
                          setSelectedCandidate(candidate);
                          setShowSchedule(true);
                        }
                      }}
                    >
                      {scheduledMap[candidate.CandidateEmail?.toLowerCase()] ? (
                        <>
                          <div className="flex justify-between items-center w-full">
                            <span>Reschedule Interview</span>
                            <span className="w-5 h-5 rounded-full border border-current flex justify-center items-center"><Pencil size={12} /></span>
                          </div>
                        </>
                      ) : (
                        "Schedule Interview"
                      )}
                    </div>



                    <div
                      className={`px-3 py-2 text-xs cursor-pointer transition-colors 
    ${interviewTakenMap[candidate.CandidateEmail?.toLowerCase()]
                          ? "bg-orange-100 text-orange-800 cursor-not-allowed"
                          : "hover:bg-gray-50"
                        }`}
                      onClick={() => {
                        const email = candidate.CandidateEmail?.toLowerCase();
                        if (interviewTakenMap[email]) {
                          toast.info("Interview already taken for this candidate.");
                          return;
                        }
                        setSelectedCandidate(candidate);
                        setShowQuestions(true);
                      }}
                    >
                      {interviewTakenMap[candidate.CandidateEmail?.toLowerCase()]
                        ? "Interview Taken"
                        : "Take Interview"}
                    </div>

                    <div
                      className={`px-3 py-2 text-xs cursor-pointer transition-colors ${decisionStatus === "Selected"
                        ? "bg-green-100 text-green-800 cursor-not-allowed"
                        : decisionStatus === "Rejected"
                          ? "bg-red-100 text-red-800 cursor-not-allowed"
                          : "hover:bg-gray-50"
                        }`}
                      onClick={() => {
                        if (decisionStatus) {
                          toast.info(`Candidate already marked as ${decisionStatus}`);
                        } else {
                          openDecisionModal(candidate);
                        }
                      }}
                    >
                      {decisionStatus || "Decision"}
                    </div>

                    {decisionStatus === "Selected" && (
                      <div>
                        {/* Offer Letter Buttons */}
                        <div className="flex justify-between items-center gap-2 w-full">
                          {!isOfferSent ? (
                            // First-time offer
                            <div
                              title="Send Offer Letter"
                              className="px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-gray-50"
                              onClick={() => openOfferForm(candidate)}
                            >
                              Send Offer
                            </div>
                          ) : (
                            <>
                              {/* View Offer */}
                              {offersById[candidate.id]?.pdfFileName && (
                                <div
                                  title="View Offer Letter"
                                  className="px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-gray-50 text-blue-600 font-medium"
                                  onClick={() =>
                                    window.open(
                                      `${apiUrl}/pdfs/${offersById[candidate.id].pdfFileName}`,
                                      "_blank"
                                    )
                                  }
                                >
                                  View Offer
                                </div>
                              )}

                              {/* Reoffer Pencil */}
                              <span
                                title="Edit Offer"
                                className="w-5 h-5 rounded-full border border-current flex justify-center items-center"
                                onClick={() => openOfferForm(candidate)}
                              >
                                <Pencil size={12} />
                              </span>
                            </>
                          )}
                        </div>

                        {isOfferSent && (
                          <div className="flex justify-between items-center gap-2 w-full">
                            {!joinedMap[candidate.CandidateEmail?.toLowerCase()] ? (
                              // First-time joining
                              <div
                                title="Mark Candidate as Joined"
                                className="px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-gray-50"
                                onClick={() => {
                                  setSelectedCandidate(candidate);
                                  setShowJoiningForm(true);
                                }}
                              >
                                Mark Joined
                              </div>
                            ) : (
                              <>
                                {/* View Appointment Letter */}
                                {joinedMap[candidate.CandidateEmail?.toLowerCase()]?.pdfFileName && (
                                  <div
                                    title="View Appointment Letter"
                                    className="px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-gray-50 text-blue-600 font-medium"
                                    onClick={() =>
                                      window.open(
                                        `${apiUrl}/pdfs/${joinedMap[candidate.CandidateEmail.toLowerCase()].pdfFileName}`,
                                        "_blank"
                                      )
                                    }
                                  >
                                    View Letter
                                  </div>
                                )}

                                {/* Rejoin Pencil */}
                                <span
                                  title="Rejoin"
                                  className="w-5 h-5 rounded-full border border-current flex justify-center items-center"
                                  onClick={() => {
                                    setSelectedCandidate(candidate);
                                    setShowJoiningForm(true);
                                  }}
                                >
                                  <Pencil size={12} />
                                </span>
                              </>
                            )}
                          </div>
                        )}

                      </div>
                    )}

                    <div
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setShowAssetsModal(true);
                      }}
                      className="px-3 py-2 text-xs cursor-pointer transition-colors hover:bg-gray-50">
                      Company's Assets
                    </div>

                    {/* Delete */}
                    <div
                      className="px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer transition-colors"
                      onClick={() => handleDelete(candidate.id)}
                    >
                      <Trash2 size={14} /> Delete
                    </div>
                  </div>
                )}
              </div>
            </div>


          </div>
        );
      })}

      {/* Modals */}
      {showSchedule && selectedCandidate && (
        <ScheduleInterview
          candidate={selectedCandidate}
          onClose={() => setShowSchedule(false)}
          onSubmit={(data) => {
            if (data.CandidateEmail) {
              setScheduledMap((prev) => ({
                ...prev,
                [data.CandidateEmail.toLowerCase()]: true
              }));
            }
            if (refreshCandidates) refreshCandidates();
          }}
        />
      )}

      {showOfferModal && currentCandidateForPDF && (
        <OfferLetterModal
          onClose={() => setShowOfferModal(false)}>
          <iframe
            src={`http://localhost:7000/pdfs/${currentCandidateForPDF.pdfFileName}`}
            className="w-full h-full"
            title={`Offer Letter for ${currentCandidateForPDF.CandidateName}`}
          />
        </OfferLetterModal>
      )}




      {showQuestions && selectedCandidate && (
        <QuestionForm
          candidate={selectedCandidate}
          onClose={() => {
            setShowQuestions(false);
            setSelectedCandidate(null);
          }}
          onInterviewTaken={(email) => {
            setInterviewTakenMap((prev) => ({
              ...prev,
              [email]: true,   // ✅ is candidate ka interview taken mark
            }));
            // toast.success("Interview marked as taken!");
          }}
        />
      )}


      {showDecision && selectedCandidate && (
        <DecisionModal
          candidate={selectedCandidate}
          decisionData={decisionData}
          onChange={handleDecisionChange}
          onSubmit={handleDecisionSubmit}
          onClose={() => setShowDecision(false)}
          loading={loading}
        />
      )}

      {showOfferForm && selectedCandidate && (
        <OfferLetterModal
          candidate={selectedCandidate}
          offerData={offerData}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onClose={closeForm}
          loading={loading}
        />
      )}
      {showJoiningForm && selectedCandidate && (
        <Joining
          prefillData={selectedCandidate}
          onClose={() => {
            setShowJoiningForm(false);
            setSelectedCandidate(null);
          }}
        />
      )}

      {showAssetsModal && selectedCandidate && (
        <CompanyAssets
          candidate={selectedCandidate}
          onClose={() => {
            setShowAssetsModal(false);
            setSelectedCandidate(null);
          }}
        />
      )}

      {showDeleteModal && candidateToDelete && (
        <DeleteConfirmationModal
          candidate={candidateToDelete}
          onConfirm={confirmDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setCandidateToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default OnboardingCards;
